import styles from '../../assets/index.css?inline';
import { Search as SearchWidget } from '@/components/SearchWidget';
import { SearchProps } from '@/types/search';
import {
  createEffect,
  createSignal,
  onCleanup,
  onMount,
  Show,
  ErrorBoundary,
} from 'solid-js';
import { EventType, WIDGET_TYPE } from '@/types/constants';
import { SlideOverPanel } from '@/components/Slider';
import { HelpIcon } from '@/components/Icons';
import { EventTracker, eventTrackerStore } from '@/utils/event-tracker-store';
import { getEventTracker } from '@/utils/trackEvent';
import { injectBrainfishStyles } from '@/utils/inject-brainfish-styles';
import { Logger } from '@/utils/logger';

export type StandardProps = SearchProps & {
  widgetType?: WIDGET_TYPE;
  input?: HTMLElement;
  onToggle?: (value: boolean) => void;
};

export const Standard = (
  props: StandardProps,
  { element }: { element: HTMLElement },
) => {
  const widgetType = props.widgetType || WIDGET_TYPE.SEARCH;
  const [isWidgetDisplayed, setIsWidgetDisplayed] = createSignal(false);
  const [panelIsOpen, setPanelIsOpen] = createSignal(false);
  const settings = props.settings || {};
  const apiHost = props.apiHost || 'https://app.brainfi.sh';
  const [mounted, setMounted] = createSignal(false);
  const [answering, setAnswering] = createSignal(false);
  const [hostElementCss, setHostElementCss] = createSignal('');
  const [eventTracker, setEventTracker] = eventTrackerStore;

  const initializeTracker = async (disableTracking: boolean) => {
    const isChromeExtension = props.isChromeExtension || false;
    const source = isChromeExtension ? WIDGET_TYPE.AGENT_ASSIST : widgetType;
    const eventTrackerProps = {
      apiHost,
      apiKey: props.apiKey ?? '',
      source,
      skip: disableTracking,
      accessToken: props.accessToken,
    };

    const eventTracker: EventTracker = await getEventTracker(eventTrackerProps);

    // Set the eventTracker in the store
    setEventTracker((prev) => ({
      ...prev,
      ...eventTracker, // eventTracker contains the new properties
    }));

    eventTracker.track({
      name: 'Widget loaded',
      type: EventType.WidgetLoaded,
      data: {},
    });
  };

  createEffect(async () => {
    const { disableTracking } = props;

    if (
      !mounted() ||
      disableTracking ||
      (!props.apiKey && !props.accessToken)
    ) {
      return;
    }

    try {
      await initializeTracker(!!disableTracking);
    } catch (error) {
      Logger.warn('Error initializing tracker', error);
    }
  });

  const launchSearch = () => {
    setIsWidgetDisplayed(true);
  };

  const searchLauncherObserver = new IntersectionObserver((intersections) => {
    if (intersections.some((intersection) => intersection.isIntersecting))
      launchSearch();
  });

  onMount(() => {
    searchLauncherObserver.observe(element);
    setMounted(true);
  });

  onCleanup(() => {
    searchLauncherObserver.disconnect();
  });

  const handleSlideOverPanelToggle = () => {
    setPanelIsOpen(!panelIsOpen());
    const trackName = panelIsOpen()
      ? 'Open widget panel'
      : 'Close widget panel';
    const type = panelIsOpen() ? EventType.OpenWidget : EventType.CloseWidget;
    eventTracker.track({ name: trackName, type, data: {} });
    if (props.onToggle) {
      props.onToggle(panelIsOpen());
    }
  };

  createEffect(() => {
    if (props.theme) {
      setHostElementCss(injectBrainfishStyles(props.theme));
    }
  });

  return (
    <ErrorBoundary fallback={(err) => err}>
      <style>
        {styles}
        {hostElementCss()}
      </style>
      <Show when={isWidgetDisplayed()}>
        <Show when={props.widgetType === WIDGET_TYPE.SEARCH}>
          <SearchWidget {...props} eventTracker={eventTracker} />
        </Show>
        <Show when={props.widgetType === WIDGET_TYPE.HELP}>
          {panelIsOpen() && (
            <SlideOverPanel
              panelTitle="Help"
              isOpen={panelIsOpen()}
              onToggle={handleSlideOverPanelToggle}
              apiHost={apiHost}
              apiKey={props.apiKey ?? ''}
              answering={answering()}
              {...settings}
            >
              <SearchWidget
                {...props}
                theme={props.theme}
                onAnswer={setAnswering}
                eventTracker={eventTracker}
              />
            </SlideOverPanel>
          )}
          {props.input ? (
            <div on:click={() => handleSlideOverPanelToggle()}>
              {typeof props.input === 'string' ? (
                <div innerHTML={props.input} />
              ) : (
                props.input
              )}
            </div>
          ) : (
            <button
              type="button"
              class="py-2.5 px-5 mr-2 mb-2 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 focus:z-10"
              onClick={() => handleSlideOverPanelToggle()}
            >
              <HelpIcon class="inline-block w-5 h-5 mr-1 -mt-0.5" />
              {props.settings?.buttonText
                ? props.settings?.buttonText
                : 'Help & Support'}
            </button>
          )}
        </Show>
      </Show>
    </ErrorBoundary>
  );
};
